<template>
    <div class="moreMarketDisplay">
        <div
            class="moreMarketDisplay_type"
            v-bind:class="{ _isMarketShow: isMarketShow }"
            v-on:click="isMarketShow = !isMarketShow"
        >
            <span>
                {{ marketTypeDisplayTitle }}
            </span>
            <SvgIcon iconName="systemIcon_arrowBottom" />
        </div>
        <div v-if="isMarketShow" class="moreMarketDisplay_columnWrapper">
            <div class="moreMarketDisplay_content">
                <div
                    v-for="(priceGroups, index) in groupFastMarketOdds"
                    v-bind:key="index"
                    class="_fastMarketgroup"
                >
                    <div
                        v-for="(group, key) in priceGroups"
                        v-bind:key="key"
                        class="moreMarketDisplay_oddsGroup"
                    >
                        <div class="moreMarketDisplay_columnName">
                            {{ key }}
                        </div>
                        <FastMarketOdds
                            v-for="price in group.priceOptions"
                            v-bind:key="price.option"
                            v-bind:oddsInfo="group.oddsInfo"
                            v-bind:priceOption="price"
                            v-bind:matchInfo="match"
                            v-bind:marketOrder="marketOrder"
                            v-bind:displaySection="displaySection"
                            class="moreMarketDisplay_odds"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import orderBy from 'lodash/orderBy';
    import type { PropType } from 'vue';
    import { computed, defineComponent, onMounted, ref, toRefs, watch } from 'vue';
    import FastMarketOdds from '@/components/match/FastMarketOdds.vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { IOdds } from '@/interface/IOdds';
    import { DisplaySection } from '@/interface/enum';
    import { getFastMarketDisplayName } from '@/libraries/helpers/oddsHelper';
    import { i18n } from '@/libraries/i18n';
    import { isOneMinFastMarket } from '@/libraries/oddsDisplay';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'FastMarket',
        components: {
            FastMarketOdds,
            SvgIcon,
        },
        props: {
            oddsList: {
                type: Array as PropType<IOdds[]>,
                required: true,
            },
            match: {
                type: Object as PropType<IMatch>,
                required: true,
            },
            marketOrder: {
                type: Number,
                default() {
                    return -1;
                },
            },
            isCollapse: {
                type: Boolean,
                default() {
                    return false;
                },
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
        },
        setup(props) {
            const { oddsList } = toRefs(props);
            const is1MinuteFastMarket = computed(() => isOneMinFastMarket(oddsList.value[0].marketGroup.id));
            const marketTypeDisplayTitle = computed(() => (is1MinuteFastMarket.value
                ? i18n.t('one_Minute_Fast_Market')
                : i18n.t('five_Minutes_Fast_Market')));

            const marketTypeDisplayName = (marketGroupId: number) => getFastMarketDisplayName(marketGroupId);
            const groupFastMarketOdds = computed(() => {
                const sortedOdds = orderBy(oddsList.value, odds => odds.marketGroup.id);
                const groupFastMarketOdds = sortedOdds.reduce<Array<Record<string, { oddsInfo: IOdds, priceOptions: IOdds['priceOption'] }>>>((groupOdds, odds, index) => {
                    groupOdds[index % 3][marketTypeDisplayName(odds.marketGroup.id)] = {
                        oddsInfo: odds,
                        priceOptions: odds.priceOption,
                    };

                    return groupOdds;
                }, [{}, {}, {}]);
                return groupFastMarketOdds;
            });

            const isMarketShow = ref(false);

            onMounted(() => {
                isMarketShow.value = props.marketOrder === 1;
            });

            watch(
                () => props.isCollapse,
                (newVal) => {
                    isMarketShow.value = newVal;
                },
            );

            return {
                isMarketShow,

                marketTypeDisplayTitle,
                groupFastMarketOdds,
            };
        },
    });
</script>

<style lang="scss" scoped>
.moreMarketDisplay {
    background: $moreMarketDisplay_bg;

    &_type {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.7rem 1rem;
        margin-bottom: 0.1rem;
        background: $moreMarketDisplay_type_bg;
        color: #4b4b4b;
        font-weight: bold;

        .svgIcon {
            width: 1.2em;
            height: 1.2em;
            fill: rgba($moreMarketDisplay_type_color, 0.7);
            transition: transform 0.3s;
        }

        &._isMarketShow .svgIcon {
            transform: rotate(-180deg);
        }
    }

    &:last-child > &_type {
        margin-bottom: 0;
    }

    &_content {
        display: flex;
        flex: 1 1;
        flex-direction: row;
    }

    &_oddsGroup {
        flex: 1;
        padding-right: 3%;
        padding-left: 7%;
        text-align: center;

        &:last-child {
            border: none;
        }
    }

    &_columnWrapper {
        display: flex;
        flex-direction: column;
    }

    &_columnName {
        display: flex;
        flex-direction: row;
        justify-content: space-around;
        padding: 0.5em 0.3em 0;
        color: $moreMarketDisplay_columnName_color;
        text-align: center;
    }

    &_odds {
        flex-direction: row;
        align-items: center;
        margin: 0.5em 0.5em 0 0;
    }
}

.__live {
    &.moreMarketDisplay {
        background: $moreMarketDisplay_bg_live;

        .moreMarketDisplay_type {
            background: $moreMarketDisplay_type_bg_live;
        }
    }
}

._rotate_table {
    &.moreMarketDisplay {
        .moreMarketDisplay_columnWrapper {
            flex-direction: row;
        }

        .moreMarketDisplay_columnName {
            flex-direction: column;
            padding-left: 1rem;
            text-align: left;
        }

        .moreMarketDisplay_content {
            flex-direction: column;
        }
    }
}

._fastMarketgroup {
    flex: 1;
    padding-bottom: 1em;
    background-color: #ffedee;

    &:first-child,
    &:last-child {
        background-color: $red-bg-2;
    }
}
</style>
